import cx from 'classnames'
import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  useState,
} from 'react'
import { type Noop, type RefCallBack } from 'react-hook-form'

export interface InputChangeEvent {
  target: EventTarget & (HTMLInputElement | HTMLTextAreaElement)
  type?: string
}

type InputChangeHandler = (event: InputChangeEvent) => Promise<void | boolean>
type ChangeHandler = (event: ChangeEvent) => void

// Combination of ControllerRenderProps and ControllerRenderProps from react-hook-form
export interface CustomFormRegister {
  ref: RefCallBack
  name: string
  value?: string | number
  onChange: InputChangeHandler | ChangeHandler | Noop
  onBlur: InputChangeHandler | Noop
}

interface InputFieldProps {
  type: 'text' | 'email' | 'number' | 'password'
  errorMessage?: string
  formRegister: CustomFormRegister
  label?: string
  isMinimal?: boolean
  isUnstyled?: boolean
  inputClassName?: string
  children?: ReactNode
}

const InputField = ({
  id,
  type,
  value,
  defaultValue = '',
  autoComplete,
  placeholder,
  max,
  maxLength,
  label,
  isMinimal,
  isUnstyled,
  errorMessage,
  formRegister,
  className,
  inputClassName,
  children,
}: InputFieldProps & InputHTMLAttributes<HTMLInputElement>) => {
  const isManaged = typeof value !== 'undefined'
  const [currentValue, setCurrentValue] = useState(defaultValue)

  const isEmail = type === 'email'
  const isNumber = type === 'number'

  const inputMode = isEmail ? 'email' : isNumber ? 'decimal' : 'text'

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    formRegister.onBlur(event)
    setCurrentValue(event.target.value)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    formRegister.onChange(event)
    setCurrentValue(event.target.value)
  }

  if (isUnstyled) {
    return (
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        max={max}
        maxLength={maxLength}
        autoComplete={autoComplete}
        ref={formRegister.ref}
        name={formRegister.name}
        value={isManaged ? value : currentValue}
        placeholder={placeholder}
        onBlur={handleBlur}
        onChange={handleChange}
        className={cx(inputClassName, className)}
      />
    )
  }

  return (
    <div className={cx('grid', className)}>
      <div className="flex flex-col gap-y-1 relative text-left">
        {label && (
          <label htmlFor={id} className="text-xs uppercase">
            {label}
          </label>
        )}

        <div
          className={cx('flex justify-between', {
            border: !isMinimal,
            'border-b': isMinimal,
            'border-input-border': !errorMessage && !isMinimal,
            'border-input-minimal-border': !errorMessage && isMinimal,
            'border-error': errorMessage,
          })}
        >
          <input
            id={id}
            type={type}
            inputMode={inputMode}
            max={max}
            maxLength={maxLength}
            autoComplete={autoComplete}
            ref={formRegister.ref}
            name={formRegister.name}
            value={isManaged ? value : currentValue}
            placeholder={placeholder}
            onBlur={(event) => handleBlur(event)}
            onChange={(event) => handleChange(event)}
            className={cx(
              'relative appearance-none w-full h-full text-sm leading-none',
              'bg-input-bg text-input-text',
              {
                'text-sm py-4 px-3': !isMinimal,
                'text-xs py-3': isMinimal,
              },
              inputClassName
            )}
          />
          {children}
        </div>

        {errorMessage && (
          <span role="alert" className="text-xs text-error">
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
}

export default InputField
